/**
 * Contacts list page — per-customer phonebook. Used as the
 * internal-CID source on extension / ring-group dial nodes.
 *
 * Customer-scoped (super_admin / reseller_owner need an entered
 * customer context to see the list — same pattern as Time Frames,
 * Auth Lists, etc.). Office_manager and below see their own
 * customer's contacts automatically via RLS.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contacts, type ContactRow } from '@/api/contacts';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { useToast } from '@/hooks/use-toast';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Plus, MoreHorizontal, Pencil, Trash2, UserSquare, AlertTriangle,
} from 'lucide-react';
import { PhoneInput } from '@/components/PhoneInput';

const CAN_MUTATE = ['super_admin', 'reseller_owner', 'office_manager'];
const E164_RE = /^\+[1-9]\d{6,14}$/;

export function ContactsPage() {
  const user = useCurrentUser();
  const effectiveCustomerId = useEffectiveCustomerId();
  const qc = useQueryClient();
  const { toast } = useToast();
  const canMutate = CAN_MUTATE.includes(user.role);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ContactRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ContactRow | null>(null);
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['contacts', effectiveCustomerId, search],
    queryFn: () => contacts.list({
      customer_id: effectiveCustomerId ?? undefined,
      search: search.trim() || undefined,
      limit: 500,
    }),
  });
  const rows: ContactRow[] = useMemo(() => data?.rows ?? [], [data]);

  const remove = useMutation({
    mutationFn: (id: string) => contacts.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      toast({ title: 'Contact deleted' });
      setDeleteTarget(null);
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not delete',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  const columns = [
    {
      header: 'Ref',
      cell: (r: ContactRow) => (
        <span className="font-mono text-xs text-muted-foreground">CT-{r.ref_number}</span>
      ),
    },
    {
      header: 'Name',
      cell: (r: ContactRow) => (
        <span className="font-medium">{r.display_name}</span>
      ),
    },
    {
      header: 'Internal CID',
      cell: (r: ContactRow) => (
        <span className="font-mono text-xs">
          {r.internal_cid_phone ?? <span className="text-muted-foreground">—</span>}
        </span>
      ),
    },
    {
      header: 'External',
      cell: (r: ContactRow) => (
        <span className="font-mono text-xs">
          {r.external_phone ?? <span className="text-muted-foreground">—</span>}
        </span>
      ),
    },
    {
      header: 'Email',
      cell: (r: ContactRow) => (
        <span className="text-xs">
          {r.email ?? <span className="text-muted-foreground">—</span>}
        </span>
      ),
    },
    {
      header: 'Status',
      cell: (r: ContactRow) =>
        r.active
          ? <Badge variant="default" className="text-[10px]">active</Badge>
          : <Badge variant="secondary" className="text-[10px]">inactive</Badge>,
    },
    {
      header: '',
      cell: (r: ContactRow) =>
        canMutate ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEditTarget(r)}>
                <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setDeleteTarget(r)}
                className="text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Contacts"
        description="Per-customer phonebook used as the internal CID on extensions and ring groups."
        actions={canMutate ? (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" /> New contact
          </Button>
        ) : undefined}
      />

      <div className="mb-4 max-w-sm">
        <Input
          placeholder="Search by name, phone, or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        empty={isLoading ? 'Loading…' : 'No contacts yet.'}
      />

      {createOpen && (
        <ContactDialog
          mode="create"
          customerId={effectiveCustomerId}
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['contacts'] });
            // The picker queries also cache by customerId — invalidate
            // so a freshly-created contact is immediately pickable in
            // the extension / ring-group editors.
            qc.invalidateQueries({ queryKey: ['contacts', 'picker'] });
            setCreateOpen(false);
          }}
        />
      )}

      {editTarget && (
        <ContactDialog
          mode="edit"
          initial={editTarget}
          customerId={effectiveCustomerId}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['contacts'] });
            qc.invalidateQueries({ queryKey: ['contacts', 'picker'] });
            setEditTarget(null);
          }}
        />
      )}

      {deleteTarget && (
        <Dialog open onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                Delete contact?
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm py-2">
              Delete <span className="font-medium">{deleteTarget.display_name}</span>{' '}
              <span className="font-mono text-xs text-muted-foreground">
                (CT-{deleteTarget.ref_number})
              </span>
              ? Any extensions or ring groups currently linked to this
              contact will fall back to their override / external CID.
            </p>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" disabled={remove.isPending}>Cancel</Button>
              </DialogClose>
              <Button
                variant="destructive"
                onClick={() => remove.mutate(deleteTarget.id)}
                disabled={remove.isPending}
              >
                {remove.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ── Create / edit dialog ───────────────────────────────────────────────────

function ContactDialog({
  mode,
  initial,
  customerId,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit';
  initial?: ContactRow;
  customerId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [displayName, setDisplayName] = useState(initial?.display_name ?? '');
  const [internalCid, setInternalCid] = useState(initial?.internal_cid_phone ?? '');
  const [externalPhone, setExternalPhone] = useState(initial?.external_phone ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [active, setActive] = useState(initial?.active ?? true);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        display_name: displayName.trim(),
        // Empty string → null so the column ends up NULL instead of
        // an empty string (which would fail E.164 validation on the
        // backend and also look weird in lists).
        internal_cid_phone: internalCid.trim() || null,
        external_phone: externalPhone.trim() || null,
        email: email.trim() || null,
        notes: notes.trim() || null,
        active,
      };
      if (mode === 'create') {
        return contacts.create({
          ...body,
          ...(customerId ? { customer_id: customerId } : {}),
        });
      }
      return contacts.update(initial!.id, body);
    },
    onSuccess: () => {
      toast({ title: mode === 'create' ? 'Contact created' : 'Contact saved' });
      onSaved();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Save failed.');
    },
  });

  const phonesValid =
    (!internalCid.trim() || E164_RE.test(internalCid.trim())) &&
    (!externalPhone.trim() || E164_RE.test(externalPhone.trim()));
  const canSubmit = displayName.trim().length > 0 && phonesValid && !save.isPending;

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !save.isPending) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserSquare className="h-4 w-4" />
            {mode === 'create' ? 'New contact' : 'Edit contact'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="c-name">Name <span aria-hidden>*</span></Label>
            <Input
              id="c-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Sales, John Smith"
              autoFocus
              maxLength={200}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="c-internal">Internal CID (phone)</Label>
              <PhoneInput
                id="c-internal"
                value={internalCid}
                onChange={setInternalCid}
                customerId={customerId}
                placeholder="555 123 4567"
              />
              {internalCid.trim() !== '' && !E164_RE.test(internalCid.trim()) && (
                <p className="text-[11px] text-red-500">Enter a valid phone number.</p>
              )}
              <p className="text-[11px] text-muted-foreground leading-snug">
                Number shown on the called party's handset for internal dials.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-external">External (click-to-call)</Label>
              <PhoneInput
                id="c-external"
                value={externalPhone}
                onChange={setExternalPhone}
                customerId={customerId}
                placeholder="555 123 4567"
              />
              {externalPhone.trim() !== '' && !E164_RE.test(externalPhone.trim()) && (
                <p className="text-[11px] text-red-500">Enter a valid phone number.</p>
              )}
              <p className="text-[11px] text-muted-foreground leading-snug">
                Used as a fallback when internal CID is empty.
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="c-email">Email</Label>
            <Input
              id="c-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contact@example.com"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="c-notes">Notes</Label>
            <Textarea
              id="c-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Anything you want to remember about this contact…"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <Switch checked={active} onCheckedChange={setActive} />
            <span className="text-sm">Active</span>
            <span className="text-[11px] text-muted-foreground">
              (inactive contacts are hidden from the picker on extensions and ring groups)
            </span>
          </label>

          {error && (
            <p role="alert" className="text-xs text-red-500 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={save.isPending}>Cancel</Button>
          </DialogClose>
          <Button onClick={() => { setError(null); save.mutate(); }} disabled={!canSubmit}>
            {save.isPending ? 'Saving…' : mode === 'create' ? 'Create contact' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
